import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { LoaderModule, ScrollbarModule, TableConfig, TableModule } from '@michelin/theme';
import { TablesVisualization } from 'src/app/interfaces/tableVisualization';
import { TablesVisualizationUpdate } from 'src/app/interfaces/tableVisualizationUpdate';
import { UpdatedColumns } from 'src/app/interfaces/updatedColumns';
import { ApplicationService } from '../application.service';
import { TableVisualizationModalComponent } from '../table-visualization-modal/table-visualization-modal.component';
import { TableVisualizationService } from './table-visualization.service';

// Create a new type
type RecordType = Record<string, unknown>;

@Component({
  selector: 'mic-app-table-visualization',
  templateUrl: './table-visualization.component.html',
  styleUrls: ['./table-visualization.component.scss'],
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    MatIcon,
    LoaderModule,
    TableModule,
    ScrollbarModule,
    MatPaginator
  ]
})
export class TableVisualizationComponent implements OnInit {
  private readonly tableVisualizationService = inject(TableVisualizationService);
  dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly applicationService = inject(ApplicationService);

  private readonly _800 = 800;
  user = JSON.parse(localStorage.getItem('user'));
  @Input()
  func: any;

  @Output() childToParent: EventEmitter<number> = new EventEmitter<number>();
  updatedColumns: UpdatedColumns[] = [];

  filterColumns: FormGroup;
  filterConf: any = {};

  primaryKey: string[] = [];

  tableVisualization: TablesVisualization;
  panelOpenState = false;
  data: any[] = [];
  attributes: string[];
  dataSource = new MatTableDataSource();
  tableColumns: any = {};
  idKey: string;
  tableConfig: TableConfig<any> = {
    columns: {}
  };
  page = 0;
  length = 10;
  pageSize = 10;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  paginationSpinner = true;

  limit: number;
  offset: number;
  tableVisualizationUpdated: TablesVisualizationUpdate = {
    tableVisualization: null,
    connectionDBS: []
  };
  ngOnInit(): void {
    this.tableVisualization = this.func;
    this.tableVisualizationUpdated.connectionDBS = this.applicationService.connectionDB;
    this.tableVisualizationUpdated.tableVisualization = this.tableVisualization;
    // Here is to see the attribute if there is  primary key value
    this.tableVisualization.columnCsvList.forEach((column) => {
      if (column.filterable) {
        this.filterConf[column.name] = new FormControl(null);
      }
      if (column.primaryKey) {
        this.primaryKey.push(column.name);
      }
    });
    this.filterColumns = new FormGroup(this.filterConf);
    this.pageSize = 10;
    this.tableVisualizationService
      .getTableData(this.tableVisualization.id, this.page, this.pageSize, JSON.stringify(this.filterColumns.value))
      .subscribe((response) => {
        this.data = this.normalizeData(response.data);
        this.length = response.count;

        this.dataSource.data = this.data;
        this.cdr.detectChanges();
        const attributes = Object.keys(this.data[0]);
        this.idKey = attributes[0];
        attributes.forEach((attribute, index) => {
          const column = this.tableVisualization.columnCsvList.find((c) => c.name === attribute);
          const isSticky = index < 3;

          if (column?.updatable) {
            this.tableColumns[attribute] = {
              cell: attribute,
              header: attribute,
              sticky: isSticky
            };
          } else {
            this.tableColumns[attribute] = {
              cell: (elm: any) => elm[attribute],
              header: attribute,
              sticky: isSticky
            };
          }
        });

        this.tableConfig = {
          columns: this.tableColumns
        };

        this.paginationSpinner = false;
        this.cdr.detectChanges();
      });
  }

  private normalizeData(data: any[]): any[] {
    return data.map((row) => {
      const normalized: any = {};
      for (const key of Object.keys(row)) {
        const value = row[key];
        normalized[key] = value === null || value === undefined ? value : String(value);
      }
      return normalized;
    });
  }

  onGetData(): void {
    this.tableVisualizationService
      .getTableData(this.tableVisualization.id, 0, this.pageSize, JSON.stringify(this.filterColumns.value))
      .subscribe((response) => {
        this.data = this.normalizeData(response.data);
        this.length = response.count;
        this.dataSource.data = this.data;
        this.cdr.detectChanges();
        this.paginator.pageIndex = 0;
      });
  }
  onPageChange($event: PageEvent): void {
    this.dataSource.paginator = null;
    this.paginationSpinner = true;
    this.limit = $event.pageIndex * $event.pageSize + 1;
    this.offset = $event.pageSize - 1 + this.limit;
    if (this.offset > this.length) {
      this.offset = this.length;
    }
    this.tableVisualizationService
      .getTableData(this.tableVisualization.id, this.limit, this.offset, JSON.stringify(this.filterColumns.value))
      .subscribe((response) => {
        this.data = this.normalizeData(response.data);
        this.dataSource.data = this.data;
        this.cdr.detectChanges();
        this.paginationSpinner = false;
      });
  }

  onWhereclause(data: Record<string, unknown>): string {
    let whereClause = '';
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (whereClause !== '') {
          whereClause += ' AND ';
        }
        whereClause += `${key} = '${data[key]}'`;
      }
    }
    return whereClause;
  }

  getPrimaryKeyAttributes(record: RecordType, primaryKey: string[]): RecordType {
    const primaryKeyAttributes: RecordType = {};
    primaryKey.forEach((key) => {
      primaryKeyAttributes[key] = record[key];
    });
    return primaryKeyAttributes;
  }

  getInputDisplaySize(value: unknown): number {
    const minVisibleChars = 6;
    const maxVisibleChars = 18;
    const contentLength = String(value ?? '').length;
    return Math.min(Math.max(contentLength, minVisibleChars), maxVisibleChars);
  }

  onChange($event: Event, data: Record<string, unknown>, culmnUpdated: string): void {
    const whereClauseToSend: RecordType = this.getPrimaryKeyAttributes(data, this.primaryKey);

    const styleId = this.onWhereclause(data);
    const stylColumn = document.getElementById(`${culmnUpdated}-${data[this.idKey]}-${styleId}`);
    stylColumn.style.backgroundColor = 'red';
    const dataUpdated: UpdatedColumns = {
      id: data[this.idKey] as number,
      key: this.idKey,
      styleId,
      whereClause: whereClauseToSend,
      tableName: this.tableVisualization.tableName,
      columnName: culmnUpdated,
      // @ts-expect-error : avoid this line to be removed
      newValue: $event.target.value
    };
    this.updatedColumns.push(dataUpdated);
  }

  onSubmit(): void {
    this.onGetData();
  }

  onUpdateData(): void {
    this.tableVisualizationService.updateTableData(this.tableVisualization.id, this.updatedColumns).subscribe(() => {
      this.removeStyle();
      setTimeout(() => {
        this.updatedColumns.forEach((elm) => {
          const cell = document.getElementById(`${elm.columnName}-${elm.id}-${elm.styleId}`);
          cell.style.backgroundColor = 'White';
          cell.style.color = 'black';
        });
        this.updatedColumns.length = 0;
      }, 1000);
    });
  }

  removeStyle(): void {
    this.updatedColumns.forEach((elm) => {
      const cell = document.getElementById(`${elm.columnName}-${elm.id}-${elm.styleId}`);
      cell.style.backgroundColor = 'Green';
    });
  }

  onDelete() {
    this.tableVisualizationService.deleteTable(this.tableVisualization.id).subscribe(() => {
      this.childToParent.emit(this.tableVisualization.id);
    });
  }
  onUpdateSettings(): void {
    const dialogRef = this.dialog.open(TableVisualizationModalComponent, {
      width: '700px',
      data: this.tableVisualizationUpdated
    });
    dialogRef.afterClosed().subscribe((table: TablesVisualization | boolean) => {
      if (table !== undefined && table !== false) {
        this.paginationSpinner = true;
        this.func = table as TablesVisualization;
        this.tableColumns = {};

        this.ngOnInit();
      }
    });
  }
}
